import { HTMLAttributes, ReactElement, useCallback, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useCopyToClipboard } from "react-use";
import { Clear, Search } from "@mui/icons-material";
import {
  accordionSummaryClasses,
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  listItemSecondaryActionClasses,
  MenuItem,
  menuItemClasses,
  OutlinedInput,
  Skeleton,
  Stack,
  styled,
  Typography as T,
  useTheme,
} from "@mui/material";
import EmptyStateText from "datasets/components/EmptyStateText";
import { TableLineageModal } from "datasets/components/TableLineageModal";
import useDataset from "datasets/hooks/useDataset";
import useDatasetTableFQNs from "datasets/hooks/useDatasetTableFQNs";
import useIsMasterDataset from "datasets/hooks/useIsMasterDataset";
import usePermissions from "datasets/hooks/usePermissions";
import Accordion, {
  AccordionDetails,
  AccordionSummary,
} from "datasets/pages/tables/components/Accordion";
import useActiveIngests from "datasets/pages/tables/hooks/useActiveIngests";
import useDatasetTables, { PAGE_SIZE } from "datasets/pages/tables/hooks/useDatasetTables";
import IngestionStatus, {
  DISPLAY_INGEST_STATUS,
} from "datasets/pages/tables/types/enums/ingest-status";
import DatasetTableResponse from "datasets/pages/tables/types/interfaces/table";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import pluralize from "pluralize";
import stripMarkdown from "strip-markdown";
import { ImportIcon } from "~/assets/icons";
import ActionsMenu from "~/components/ActionsMenu";
import CT from "~/components/ClampedTypography";
import EmptyState from "~/components/EmptyState";
import { getCodeSnippet } from "~/components/GenerateCodeButton";
import LimitedPagination from "~/components/LimitedPagination";
import { PreviewButton } from "~/components/table-preview/PreviewButton";
import { TablePreviewModal } from "~/components/table-preview/TablePreviewModal";
import useErrorToast from "~/contexts/error-toast";
import rumClient, { trackEvent } from "~/utils/monitoring";

const { VITE_CATALOG_API } = import.meta.env;

const Skeletons = () => (
  <>
    {Array.from({ length: 6 }).map((_, i) => (
      <Skeleton key={i} variant="rounded" height={90} sx={{ "&:not(:first-of-type)": { mt: 1 } }} />
    ))}
  </>
);

const StyledLink = styled(RouterLink)(({ theme }) => ({
  textDecoration: "none",
  fontWeight: 600,
  color: theme.palette.text.link,
  "&:visited": {
    color: theme.palette.text.visited,
  },
  "&:hover": {
    textDecoration: "underline",
  },
}));

const EmptyTableList = () => {
  const canCreate = usePermissions("create:catalog-ingest");

  return (
    <>
      <IngestionProgress />
      <Stack alignItems="center" gap={3} mt={12} data-testid="dataset-tables-list-empty">
        {canCreate ? (
          <>
            <EmptyStateText>
              This dataset has no tables.
              <br />
              Add a table below to get started.
            </EmptyStateText>
            <Button component={RouterLink} to="new" color="primary" variant="contained">
              Add Table
            </Button>
          </>
        ) : (
          <EmptyStateText>This dataset has no tables.</EmptyStateText>
        )}
      </Stack>
    </>
  );
};

const IngestionProgress = () => {
  const { data = [] } = useActiveIngests();
  const pendingCount = data.filter((ingestion) =>
    [IngestionStatus.PENDING, IngestionStatus.IN_PROGRESS].includes(ingestion.status)
  ).length;

  if (!data.length) {
    return null;
  }

  return (
    <Accordion sx={{ mb: 3 }}>
      <AccordionSummary
        sx={{
          "&[aria-expanded='false']": {
            transition: "all 200ms ease",
            transitionProperty: "border-radius",
            transitionDelay: "200ms",
          },
          "&[aria-expanded='true']": {
            borderBottomRightRadius: 0,
            borderBottomLeftRadius: 0,
          },
          [`& .${accordionSummaryClasses.content}`]: { alignItems: "center", gap: 1.5 },
        }}
      >
        <ImportIcon />
        <T variant="body1" fontWeight={500}>
          {pendingCount > 0
            ? `Importing ${pendingCount} ${pluralize("tables", pendingCount)}...`
            : "Recently imported tables"}
        </T>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          pr: 3,
          pl: 6,
          bgcolor: "common.dimmedPageBackground",
          borderBottomRightRadius: "6px",
          borderBottomLeftRadius: "6px",
        }}
      >
        <List sx={{ width: "100%" }}>
          {data.map(({ id, status, table_fqn, metadata_parse_errors }) => (
            <ListItem
              key={id}
              sx={{
                alignItems: "flex-start",
                px: 0,
                "&:not(:last-of-type)": {
                  borderBottom: "1px solid",
                  borderColor: "grey.300",
                },
              }}
            >
              <T
                variant="body2"
                sx={{
                  width: 100,
                  color: status === IngestionStatus.FAILED ? "#BD002F" : "primary.dark",
                  fontWeight: status === IngestionStatus.FAILED ? 500 : 400,
                  fontStyle: status === IngestionStatus.PENDING ? "italics" : "normal",
                  opacity: [IngestionStatus.PENDING, IngestionStatus.IN_PROGRESS].includes(status)
                    ? 0.7
                    : 1,
                }}
              >
                {DISPLAY_INGEST_STATUS[status]}
              </T>
              <Stack sx={{ width: "calc(100% - 100px)" }}>
                <T variant="body2" sx={{ width: "100%" }}>
                  {table_fqn}
                </T>
                {metadata_parse_errors?.map((error) => (
                  <T key={error} variant="body2" color="#BD002F" fontWeight={500} width="100%">
                    {error}
                  </T>
                ))}
              </Stack>
            </ListItem>
          ))}
        </List>
      </AccordionDetails>
    </Accordion>
  );
};

const TableItem = ({ table, search }: { table: DatasetTableResponse; search: string }) => {
  const theme = useTheme();
  const { showError } = useErrorToast();
  const [, copy] = useCopyToClipboard();
  const { datasetId } = useParams();
  const isMasterDataset = useIsMasterDataset();
  const { data: dataset, mutate: mutateDataset } = useDataset();
  const { mutate: mutateTables } = useDatasetTables(search);
  const { data: tableFQNs, mutate: mutateFQNs } = useDatasetTableFQNs();
  const canEdit = usePermissions("edit:catalog-metadata");

  const [showActions, setShowActions] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [lineageModalOpen, setLineageModalOpen] = useState(false);

  const handleLineage = useCallback(() => {
    setShowActions(false);

    trackEvent("dataset_list_lineage_clicked", {
      dataset_id: datasetId,
      table_fqn: table?.fullyQualifiedName,
    });

    setLineageModalOpen(true);
  }, [datasetId, table?.fullyQualifiedName]);

  const handleCode = useCallback(() => {
    setShowActions(false);
    copy(
      getCodeSnippet("r", [
        {
          schema: table.database_schema,
          table: table.name,
        },
      ])
    );

    trackEvent("dataset_list_code_copied", {
      dataset_id: datasetId,
      table_fqn: table?.fullyQualifiedName,
    });
  }, [copy, datasetId, table]);

  const handleRemove = useCallback(async () => {
    setShowActions(false);

    if (tableFQNs) {
      try {
        const updatedTables = tableFQNs?.filter((fqn) => fqn !== table.fullyQualifiedName);
        const resp = await fetch(`${VITE_CATALOG_API}/datasets/${datasetId}`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ tables: updatedTables }),
        });

        if (!resp.ok) {
          throw Error("Unable to remove table from dataset");
        }

        const data = await resp.json();

        trackEvent("dataset_list_table_removed", {
          dataset_id: datasetId,
          table_fqn: table?.fullyQualifiedName,
        });

        mutateFQNs();
        mutateTables();
        mutateDataset(data);
      } catch (e: any) {
        console.error(e);
        rumClient?.recordError(e);
        showError(e.message);
      }
    }
  }, [datasetId, mutateDataset, mutateFQNs, mutateTables, showError, table, tableFQNs]);

  const menuItems: ReactElement<HTMLAttributes<HTMLInputElement>>[] = useMemo(() => {
    const actions = [
      <MenuItem key="copy-code" onClick={handleCode}>
        Copy Code
      </MenuItem>,
    ];

    const lineageAction = (
      <MenuItem key="lineage" onClick={handleLineage}>
        View Lineage
      </MenuItem>
    );

    if (dataset?.access_level === DatasetAccessLevel.OWNER) {
      actions.unshift(lineageAction);
    }

    if (canEdit) {
      actions.push(
        <MenuItem
          key="remove"
          sx={{
            display: isMasterDataset ? "none" : "block",
            color: "attention.main",
            [`&.${menuItemClasses.root}:hover`]: {
              color: "common.white",
              bgcolor: "attention.main",
            },
          }}
          onClick={handleRemove}
        >
          Remove
        </MenuItem>
      );
    }

    return actions;
  }, [canEdit, handleCode, handleLineage, handleRemove, isMasterDataset]);

  return (
    <ListItem
      data-testid="dataset-table-listitem"
      disablePadding
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      secondaryAction={
        <ActionsMenu visible={showActions} placement="bottom-end" menuItems={menuItems} />
      }
      sx={{
        py: 3,
        "&:not(:last-child)": { borderBottom: `1px solid ${theme.palette.grey[300]}` },
        [`& .${listItemSecondaryActionClasses.root}`]: {
          right: 0,
          top: 24,
          transform: "unset",
        },
        "&:first-of-type": {
          pt: 0,

          [`.${listItemSecondaryActionClasses.root}`]: {
            top: 0,
          },
        },
      }}
    >
      <Stack gap={1.5}>
        <StyledLink to={table.fullyQualifiedName}>{table.name}</StyledLink>
        <T variant="caption" color="text.secondary">
          {table.database_schema}
        </T>
        <CT component="div" sx={{ WebkitLineClamp: 4 }}>
          <Box
            className="prose prose-sm max-w-none"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: 14,
              lineHeight: "20px",
              "& > *": { m: 0, p: 0 },
              "&:empty": {
                "&::before": {
                  content: '"No description"',
                  fontStyle: "italic",
                },
              },
            }}
          >
            <ReactMarkdown remarkPlugins={[stripMarkdown]}>{table.description}</ReactMarkdown>
          </Box>
        </CT>
        <Box>
          <PreviewButton
            onOpenPreview={() => setPreviewOpen(true)}
            size="small"
            analyticsContext={{
              dataset_id: datasetId,
              table_fqn: table.fullyQualifiedName,
            }}
          />
        </Box>
      </Stack>
      <TablePreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        tableFqn={table.fullyQualifiedName}
        tableName={table.name}
        variant="compact"
      />
      <TableLineageModal
        open={lineageModalOpen}
        onOpenChange={setLineageModalOpen}
        tableFQN={table.fullyQualifiedName}
        tableName={table.name}
      />
    </ListItem>
  );
};

const DatasetTableList = () => {
  const { data: dataset } = useDataset();
  const canCreate = usePermissions("create:catalog-ingest");
  const [search, setSearch] = useState("");
  const { data: tablesList, isLoading } = useDatasetTables(search);
  const showEmpty = !isLoading && tablesList?.length === 0;

  if (showEmpty && !search) {
    return <EmptyTableList />;
  }

  return (
    <>
      <IngestionProgress />
      <Box display="flex" gap={1} justifyContent="space-between">
        <OutlinedInput
          fullWidth
          size="small"
          placeholder="Search tables..."
          startAdornment={<Search sx={{ fontSize: 18 }} />}
          endAdornment={
            search && (
              <IconButton sx={{ p: 0.25 }} onClick={() => setSearch("")}>
                <Clear fontSize="small" />
              </IconButton>
            )
          }
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ maxWidth: 400, pl: 1.25, pr: 0.75, input: { py: 0.75, pl: 0.75, pr: 0.25 } }}
        />
        {canCreate && (
          <Button component={RouterLink} to="new" variant="contained">
            Add Table
          </Button>
        )}
      </Box>
      <List disablePadding sx={{ mt: 3 }} data-testid="dataset-tables-list">
        {isLoading ? (
          <Skeletons />
        ) : (
          tablesList?.map((table) => <TableItem key={table.id} table={table} search={search} />)
        )}
        {search && showEmpty && <EmptyState />}
      </List>
      {!search && (
        <LimitedPagination total={dataset?.table_count ?? 0} defaultPageSize={PAGE_SIZE} />
      )}
    </>
  );
};

export default DatasetTableList;
